/**
 * @file DealsBoardCanvas.tsx
 * @description Полотно доски сделок на широком экране (от 1024): колонки
 * стадий с перетаскиванием карточек (phase09; phase19.31 — вынесено из
 * страницы, затенение края).
 *
 * Колонки не помещаются — полотно прокручивается вбок ВНУТРИ себя (страница
 * целиком не уезжает), и край, за которым есть ещё колонки, гаснет
 * (`useScrollFade`). Пустое место полотна тянется мышью (`useDragToScroll`).
 */

import { DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCorners, useSensor, useSensors } from "@dnd-kit/core";
import { useCallback } from "react";

import { DealsBoardColumn } from "./DealsBoardColumn";
import type { DealsBoardState } from "./useDealsBoard";
import { KanbanCard } from "../KanbanCard";
import { useScrollFade } from "../../ui/useScrollFade";
import { useDragToScroll } from "../../../hooks/useDragToScroll";
import type { DealDto } from "../../../lib/api-crm";

interface Props {
  board: DealsBoardState;
  onCardClick: (deal: DealDto) => void;
}

export function DealsBoardCanvas({ board, onCardClick }: Props) {
  const dragRef = useDragToScroll();
  const fade = useScrollFade<HTMLDivElement>();
  const fadeRef = fade.ref;
  // Один узел — два хука: прокрутка мышью и затенение края.
  const canvasRef = useCallback(
    (el: HTMLDivElement | null) => {
      fadeRef.current = el;
      dragRef(el);
    },
    [dragRef, fadeRef],
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={board.onDragStart} onDragEnd={board.onDragEnd}>
      <div ref={canvasRef} onScroll={fade.update} style={fade.style} className="min-w-0 cursor-grab overflow-x-auto pb-4">
        <div className="flex gap-3">
          {board.stages.map((stage) => (
            <DealsBoardColumn
              key={stage.id}
              stage={stage}
              deals={board.deals.filter((d) => d.currentStageId === stage.id)}
              onCardClick={onCardClick}
            />
          ))}
        </div>
      </div>
      <DragOverlay>{board.activeDeal ? <KanbanCard deal={board.activeDeal} dragging /> : null}</DragOverlay>
    </DndContext>
  );
}
